import React from 'react'
import { Button, Col, Divider, Form, Input, Result, message, Row, Select, Skeleton, Switch, Typography } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import * as deepObject from 'deep-object-diff'
import DynamicFields from '../../../DynamicFields'
import GenericList from '../../../GenericList'
import { FormComponent } from '../../../BaseComponent'
import { IntegrationConstants } from '../../../../helpers/enums/Integration'
import { IntegrationFormProps } from '../'
import IIntegrationSettings from '../../../../interfaces/IIntegrationSettings'
import ISpecie from '../../../../interfaces/ISpecie'
import { IDynamicBaseStruct } from '../../../../interfaces/IDynamicFields'
import { SearchRequest } from '../../../../services/api/interfaces/ApiRequest'
import { VerifySettingsRequest, UpdateSettingsRequest, RetrieveTotalPetRequest } from '../../../../services/integration/interfaces/ApiRequest'
import { normalizeDynReceiptMapToObject, normalizeToArray, normalizeToObject } from '../../../../helpers/field-mapping.helper'
import { PetStatusArray } from '../../../../interfaces/IPet'
import { IntegrationConsumer } from 'context/IntegrationContext'
import { ApplicationCheckoutTypeArray } from 'interfaces/IAdoptionApplicationCheckout'
import { sbListReceiptAccount, sbListOptInChoices, sbListLocations, sbListImportStatus, sbListPaymentMethod, sbListTransitionStatus, sbListAdoptionStatus } from 'services/integration'
import { ListReceiptAccountResponse, ListOptInChoicesResponse, ListLocationsResponse, ListImportStatusResponse, ListPaymentMethodResponse, ListTransitionStatusResponse, SbListAdoptionStatusResponse } from 'services/integration/interfaces/ApiResponse'

type IntegrationSettingsProps = IntegrationFormProps & {
  settings: IIntegrationSettings | null,
  total: number,
  species: ISpecie[] | null,

  loading: boolean,
  contentLoading: boolean,

  loadSpecies: (args: SearchRequest) => Promise<any>

  verifySettings: (args: VerifySettingsRequest) => Promise<any>,
  updateSettings: (args: UpdateSettingsRequest) => Promise<any>,

  retrieveTotalPet: (args: RetrieveTotalPetRequest) => Promise<any>,

  onSaved?: () => void
}

export default class IntegrationSettings extends FormComponent<IntegrationSettingsProps> {

  static defaultProps = {
    contentLoading: true
  }

  state = {
    loaded: false,
    loadError: null,
    saving: false,
    saved: false,

    includeFiltersMap: [] as any[],
    excludeFiltersMap: [] as any[],

    statusList: [] as any[],
    specieList: [] as any[],

    speciePricingMap: [] as any[],
    receiptAccountMap: [] as any[],
    dynReceiptAccountMap: [] as any[],
    statusMap: [] as any[],
    baseStatusMap: [] as any[],
    contractOptionsMap: [] as any[],
    locationsList: [] as any[],
    acceptableStatusList: [] as any[],
    searchStatusList: [] as any[],
    searchGroupList: [] as any[],

    // SB custom input
    // Data -> list ok
    // Error -> failed to load 
    // null -> not loaded 
    receiptAccountList: null as ListReceiptAccountResponse["Data"] | Error | null,
    optInChoicesList: null as ListOptInChoicesResponse["Data"] | Error | null,
    locationsChoiceList: null as ListLocationsResponse["Data"] | Error | null,
    importStatusList: null as ListImportStatusResponse | Error | null,
    paymentMethodList: null as ListPaymentMethodResponse["Data"] | Error | null,
    transitionStatusList: null as ListTransitionStatusResponse | Error | null,
    adoptionStatusList: null as SbListAdoptionStatusResponse | Error | null
  }

  filterFieldsRef: any
  excludeFieldsRef: any
  dynamicFieldsRef: any
  statusFieldsRef: any

  async componentDidMount() {
    const { integrationUuid, loading, settings, species, loadSpecies, verifySettings, retrieveTotalPet } = this.props

    if (!species) {
      await loadSpecies({
        options: {
          limit: 9999
        }
      })
    }

    if (integrationUuid && !loading && !settings) {
      try {
        await verifySettings({
          integration_uuid: integrationUuid,
          load_total: true
        })

        await retrieveTotalPet({
          integration_uuid: integrationUuid
        })
      } catch (err) {
        message.error(`Could not save settings. Reason: ${err}`, 20)
      }
    }
  }

  componentDidUpdate(prevProps: IntegrationSettingsProps, prevState) {
    const { settings, integrationUuid } = this.props
    const wasLoading = prevProps.loading && !this.props.loading
    const newState = Object.assign({}, this.state)

    if (!this.state.loaded && wasLoading) {
      newState.loaded = true

      // Load error
      if (this.props.error) {
        newState.loadError = this.props.error
      } else {
        newState.includeFiltersMap = normalizeToArray(settings?.filters, {
          mapping: {
            field: {
              values: {
                key: 'text',
                isArray: true
              }
            }
          }
        })
        newState.excludeFiltersMap = normalizeToArray(settings?.exclude_filters, {
          mapping: {
            field: {
              values: {
                key: 'text',
                isArray: true
              }
            }
          }
        })

        newState.speciePricingMap = normalizeToArray(settings?.specie_pricing_map, {
          mapping: {
            specie_uuid: {
              ages: {
                key: 'age_key',
                value: 'price'
              }
            }
          }
        })

        newState.statusMap = normalizeToArray(settings?.status_map, {
          mapping: {
            id: {
              status: {
                key: 'type'
              }
            }
          }
        })

        newState.receiptAccountMap = normalizeToArray(settings?.receipt_account_map)
        newState.dynReceiptAccountMap = normalizeToArray(settings?.dyn_receipt_account_map?.entries)
        newState.baseStatusMap = normalizeToArray(settings?.base_status_map)
        newState.contractOptionsMap = normalizeToArray(settings?.contract_options_map)
        newState.locationsList = normalizeToArray(settings?.locations_list)
        newState.acceptableStatusList = normalizeToArray(settings?.acceptable_status_list)
        newState.searchStatusList = normalizeToArray(settings?.search_status_list)
        newState.searchGroupList = normalizeToArray(settings?.search_group_list)

        newState.statusList = normalizeToArray(settings?.status_list)
        newState.specieList = normalizeToArray(settings?.specie_list)
      }
    }

    if (this.state.loaded) {
      if (this.state.saving && wasLoading) {
        newState.saving = false

        if (this.props.error) {
          message.error(`Could not save settings. Reason: ${this.props.error}`, 20)
        } else {
          message.success('Settings saved successfully!')
          newState.saved = true

          if (integrationUuid) {
            this.props.retrieveTotalPet({
              integration_uuid: integrationUuid
            })
          }
        }

        if (this.props.onSaved) {
          this.props.onSaved()
        }
      }
    }

    const hasChanged = Object.keys(deepObject.diff(this.state, newState)).length > 0
    if (hasChanged) {
      this.setState(newState)
    }
  }

  public hasSaved = () => this.state.saved

  handleSave = (values: any) => {
    const { integrationUuid, updateSettings } = this.props
    if (!integrationUuid) {
      message.error('Invalid Integration UUID')
      return
    }

    const listingMappings = {
      mapping: {
        mapKey: {
          key: ''
        }
      }
    }

    const filtersMappings = {
      mapping: {
        mapKey: {
          key: 'field'
        },
        subKeys: [
          {
            key: 'values',
            mapKey: {
              key: 'text',
              isArray: true
            }
          }
        ]
      }
    }

    values.locations_list = normalizeToObject(values.locations_list, listingMappings)
    values.acceptable_status_list = normalizeToObject(values.acceptable_status_list, listingMappings)
    values.search_status_list = normalizeToObject(values.search_status_list, listingMappings)
    values.search_group_list = normalizeToObject(values.search_group_list, listingMappings)

    values.status_list = normalizeToObject(values.status_list, listingMappings)
    values.specie_list = normalizeToObject(values.specie_list, listingMappings)

    values.receipt_account_map = normalizeToObject(values.receipt_account_map)
    values.base_status_map = normalizeToObject(values.base_status_map)
    values.contract_options_map = normalizeToObject(values.contract_options_map)

    values.dyn_receipt_account_map = normalizeDynReceiptMapToObject(values.dyn_receipt_account_map)

    values.status_map = normalizeToObject(values.status_map, {
      mapping: {
        mapKey: {
          key: 'id'
        },
        subKeys: [
          {
            key: 'status',
            mapKey: {
              key: 'type'
            }
          }
        ]
      }
    })
    values.specie_pricing_map = normalizeToObject(values.specie_pricing_map, {
      mapping: {
        mapKey: {
          key: 'specie_uuid'
        },
        subKeys: [
          {
            key: 'ages',
            mapKey: {
              key: 'age_key',
              value: 'price'
            }
          }
        ]
      }
    })

    values.filters = normalizeToObject(values.filters, filtersMappings)
    values.exclude_filters = normalizeToObject(values.exclude_filters, filtersMappings)

    this.setState({
      saving: true
    })
    updateSettings({
      integration_uuid: integrationUuid,
      settings: values
    })
  }

  handleFieldsChange = () => {
    // Just rerender dynamic fields component :P)
    this.filterFieldsRef?.forceUpdate()
    this.excludeFieldsRef?.forceUpdate()
    this.dynamicFieldsRef?.forceUpdate()
    this.statusFieldsRef?.forceUpdate()
  }

  private renderPetStatus = (itemKey?: number, parentKey?: number, form?: any) => {
    return (
      <Select
        allowClear={true}
        onChange={() => {
          this.statusFieldsRef?.forceUpdate()
          this.forceUpdate()
        }}
        options={PetStatusArray.map(type => {
          return {
            label: type,
            value: type
          }
        }) as any}
      />
    )
  }

  private renderSpecies = (itemKey: string | number) => {
    const { species } = this.props

    return (
      <Select
        allowClear={true}
      >
        {
          species?.map((sp, index) => {
            return (
              <Select.Option key={`species-select-${itemKey}-${index}`} value={sp.uuid}>
                {sp.name}
              </Select.Option>
            )
          })
        }
      </Select>
    )
  }

  private rendeAges = (itemKey: string | number) => {
    return (
      <Select
        allowClear={true}
        options={[
          {
            label: 'Baby',
            value: 'BABY'
          },
          {
            label: 'Young',
            value: 'YOUNG'
          },
          {
            label: 'Adult',
            value: 'ADULT'
          },
          {
            label: 'Senior',
            value: 'SENIOR'
          }
        ]}
      />
    )
  }

  private renderFosterPets = () => {
    const { integration } = this.props
    let options = [
      {
        label: 'Fixed',
        value: 'FIXED'
      },
      {
        label: 'Find',
        value: 'FIND'
      }
    ]

    if (integration?.partner_key !== 'SHELTER_BUDDY') {
      options = options.filter(opt => opt.value !== 'FIND')
    }

    return (
      <Select
        allowClear={true}
        options={options}
      />
    )
  }

  private renderIncludeFilters = () => {
    return (
      <DynamicFields
        name={['filters']}
        title="Filters"
        addLabel="Add Filters"
        addFullWidth={true}
        form={this.formRef}
        ref={(ref) => this.filterFieldsRef = ref}
        baseStruct={IntegrationConstants.FILTER_SETTINGS_STRUCT}
      />
    )
  }

  private renderExcludeFilters = () => {
    return (
      <DynamicFields
        name={['exclude_filters']}
        title="Exclude Filters"
        addLabel="Add Exclude Filters"
        addFullWidth={true}
        form={this.formRef}
        ref={(ref) => this.excludeFieldsRef = ref}
        baseStruct={IntegrationConstants.FILTER_SETTINGS_STRUCT}
      />
    )
  }

  private renderFilters = () => {
    const { integration } = this.props
    if (integration?.partner_key === 'PETFINDER' || integration?.partner_key === 'SHELTER_LUV' || integration?.partner_key === 'SHELTER_BUDDY') return null

    return (
      <>
        {this.renderIncludeFilters()}

        <Divider />

        {this.renderExcludeFilters()}

        <Divider />
      </>
    )
  }

  private renderCustomSettings = (context: any) => {
    if (!this.formRef) return null
    const { integration } = this.props
    const formValues = this.formRef.getFieldsValue();

    const customSettings = IntegrationConstants.SETTING_CUSTOM_FIELDS.find(p => p.partner_key === integration?.partner_key)

    if (customSettings?.fields.contract_options_map?.fields?.length && integration?.shelter?.contract_options) {
      customSettings.fields.contract_options_map.fields[0].options = integration.shelter.contract_options.map(option => {
        return {
          label: option.text,
          value: option.uuid
        }
      }) || []
      customSettings.fields.contract_options_map.fields[1].render = this._renderContractOptionValue
    }
    if (customSettings?.fields.receipt_account_map?.fields?.length) {
      customSettings.fields.receipt_account_map.fields[1].render = this._renderSelectReceiptAccount
    }
    if (customSettings?.fields.locations_list?.fields?.length) {
      customSettings.fields.locations_list.fields[0].render = this._renderLocationsValue
    }
    if (customSettings?.fields.search_status_list?.fields?.length) {
      customSettings.fields.search_status_list.fields[0].render = this._renderImportStatusValue
    }
    if (customSettings?.fields.base_status_map?.fields?.length) {
      customSettings.fields.base_status_map.fields[1].render = this._renderBaseStatusMapValue
    }

    if (customSettings) {
      return (
        <Row gutter={24}>
          {
            Object.keys(customSettings.fields).map(key => {
              const field = customSettings.fields[key]
              const hasPreviewValue = context?.preview?.settings && key in context.preview.settings;
              const noFormValue = !formValues || formValues[key] === undefined || formValues[key] === null || formValues[key] === '' || (Array.isArray(formValues[key]) && formValues[key].length === 0);
              const previewValue = hasPreviewValue ? context.preview.settings[key] : null;

              if (key === 'payment_method_id') {
                return (
                  <Col key={key} xs={24} md={12}>
                    <Form.Item
                      name={key}
                      label={field.label}
                      rules={[
                        {
                          required: field.required || false,
                          message: 'Required field'
                        }
                      ]}
                    >
                      {this._renderPaymentMethodId()}
                    </Form.Item>
                  </Col>
                )
              }

              if (field.type === 'list') {
                const listFields = field.fields || [
                  {
                    label: 'Value',
                    name: 'value',
                    type: 'text'
                  }
                ]

                return (
                  <Col key={key} xs={24} md={12}>

                    <GenericList listName={key} label={field.label} description={noFormValue && hasPreviewValue && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ fontWeight: 'bold', fontSize: 12 }}>Default value(s): {previewValue !== null ? JSON.stringify(previewValue) : ''}</span>
                        <span style={{ fontSize: 12, color: '#666' }}>(These values are taken from the network configuration until a new value is provided)</span>
                      </div>
                    )} fields={listFields} form={this.formRef} />

                  </Col>
                )
              }

              return (
                <Col key={key} xs={24} md={12}>

                  <Form.Item
                    name={key}
                    label={field.label}
                    rules={[
                      {
                        required: field.required || false,
                        message: 'Required field'
                      }
                    ]
                    }
                  >
                    {noFormValue && hasPreviewValue && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span style={{ fontWeight: 'bold' }}>Default value(s): {previewValue !== null ? JSON.stringify(previewValue) : ''}</span>
                        <span style={{ fontSize: 12, color: '#666' }}>(These values are taken from the network configuration until a new value is provided)</span>
                      </div>
                    )}
                    <Input
                      type={field.type === 'input' ? 'text' : field.type}
                      defaultValue={hasPreviewValue && noFormValue ? previewValue : undefined}
                      onChange={(e) => {
                        this.formRef.setFieldsValue({
                          [key]: e.target.value
                        })
                      }}
                    />
                  </Form.Item>

                </Col>
              )
            })
          }
        </Row>
      )
    }

    return null
  }

  private renderMappings = () => {
    if (!this.formRef) return null
    const { species, integration } = this.props

    const CUSTOM_BASE_STRUCT: IDynamicBaseStruct = {
      ...IntegrationConstants.BASE_SETTINGS_STRUCT
    }

    CUSTOM_BASE_STRUCT.specie_pricing_map.fields.specie_uuid.render = this.renderSpecies

    if (CUSTOM_BASE_STRUCT.specie_pricing_map.fields.ages.fields) {
      CUSTOM_BASE_STRUCT.specie_pricing_map.fields.ages.fields.age_key.render = this.rendeAges
    }

    if (CUSTOM_BASE_STRUCT.status_map.fields?.status.fields?.type) {
      CUSTOM_BASE_STRUCT.status_map.fields.status.fields.type.render = this.renderPetStatus
    }

    if (CUSTOM_BASE_STRUCT.status_map.fields?.status.fields?.value) {
      CUSTOM_BASE_STRUCT.status_map.fields.status.fields.value.render = this._renderStatusMapValue
    }

    const collapseSpecieHeaderOptions: any = species?.map(sp => {
      return {
        specie_uuid: sp.uuid,
        value: sp.name
      }
    }) || []

    return (
      <>
        {
          integration?.partner_key === 'SHELTER_BUDDY' ? (
            <>
              <Typography.Title level={5}>Status Map</Typography.Title>
              <DynamicFields
                name={['status_map']}
                form={this.formRef}
                ref={(ref) => this.statusFieldsRef = ref}
                baseStruct={CUSTOM_BASE_STRUCT}
              />

              <Divider />
            </>
          ) : null
        }

        <Typography.Title level={5}>Species Pricing Map</Typography.Title>
        <DynamicFields
          name={['specie_pricing_map']}
          form={this.formRef}
          ref={(ref) => this.dynamicFieldsRef = ref}
          baseStruct={CUSTOM_BASE_STRUCT}
          collapseHeaderOptions={collapseSpecieHeaderOptions}
        />
      </>
    )
  }

  private _renderSelectCheckoutItem = () => {
    return <Select
      options={ApplicationCheckoutTypeArray.map(type => {
        return {
          label: type,
          value: type
        }
      })}
    />
  }

  private _receiptAccountLocked = 0
  private _ensureReceiptAccountList = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return
    }
    if (!this.state.receiptAccountList && this._receiptAccountLocked === 0) {
      this._receiptAccountLocked++
      sbListReceiptAccount({ integration_uuid: this.props.integration.uuid! })
        .then(list => {
          this.setState({ receiptAccountList: list.data.data?.Data.filter(v => v.IsActive) })
        })
        .catch(e => {
          console.error(e)
          const wrapped = e instanceof Error ? e : new Error(e.message)
          this.setState({ receiptAccountList: wrapped })
        })
        .finally(() => this._receiptAccountLocked--)
    }
  }
  private _renderReceiptAccountSelect = () => {
    this._ensureReceiptAccountList()
    return this.state.receiptAccountList instanceof Error
      ? <Input type="number" placeholder={"Value"} />
      : <Select
        showSearch
        filterOption={(input, option) =>
          (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
        }
        loading={this.state.receiptAccountList == null}
        options={this.state.receiptAccountList?.map(item => {
          return {
            label: `${item.Name} (GL ${item.GlCode}) [ID ${item.Id}]`,
            value: item.Id
          }
        })}
      />
  }
  private _renderSelectReceiptAccount = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return <small>ONLY FOR SHELTER_BUDDY!</small>
    }
    return this._renderReceiptAccountSelect()
  }
  private _renderContractOptionValue = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return <Input type="text" placeholder={"Value"} />
    }
    return this._renderOptInChoicesSelect()
  }
  private _renderContractOptionReceiptAccount = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return <Input type="text" placeholder={"Value"} />
    }
    return this._renderReceiptAccountSelect()
  }
  private _optInChoicesLocked = 0
  private _ensureOptInChoicesList = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return
    }
    if (!this.state.optInChoicesList && this._optInChoicesLocked === 0) {
      this._optInChoicesLocked++
      sbListOptInChoices({ integration_uuid: this.props.integration.uuid! })
        .then(list => {
          this.setState({ optInChoicesList: list.data.data?.Data })
        })
        .catch(e => {
          console.error(e)
          const wrapped = e instanceof Error ? e : new Error(e.message)
          this.setState({ optInChoicesList: wrapped })
        })
        .finally(() => this._optInChoicesLocked--)
    }
  }
  private _renderOptInChoicesSelect = () => {
    this._ensureOptInChoicesList()
    return this.state.optInChoicesList instanceof Error
      ? <Input type="number" placeholder={"Value"} />
      : <Select
        showSearch
        filterOption={(input, option) =>
          (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
        }
        loading={this.state.optInChoicesList == null}
        options={this.state.optInChoicesList?.map(item => {
          const label = (item as any).Name || item.Description || ''
          return {
            label: label ? `${label} [ID ${item.Id}]` : `[ID ${item.Id}]`,
            value: item.Id
          }
        })}
      />
  }
  private _renderOptInChoiceValue = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return <Input type="text" placeholder={"Value"} />
    }
    return this._renderOptInChoicesSelect()
  }
  private _locationsLocked = 0
  private _ensureLocationsList = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return
    }
    if (!this.state.locationsChoiceList && this._locationsLocked === 0) {
      this._locationsLocked++
      sbListLocations({ integration_uuid: this.props.integration.uuid! })
        .then(list => {
          this.setState({ locationsChoiceList: list.data.data?.Data.filter(v => v.IsActive) })
        })
        .catch(e => {
          console.error(e)
          const wrapped = e instanceof Error ? e : new Error(e.message)
          this.setState({ locationsChoiceList: wrapped })
        })
        .finally(() => this._locationsLocked--)
    }
  }
  private _renderLocationsSelect = () => {
    this._ensureLocationsList()
    return this.state.locationsChoiceList instanceof Error
      ? <Input type="number" placeholder={"Value"} />
      : <Select
        showSearch
        filterOption={(input, option) =>
          (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
        }
        loading={this.state.locationsChoiceList == null}
        options={this.state.locationsChoiceList?.map(item => {
          return {
            label: item.Name,
            value: item.Id
          }
        })}
      />
  }
  private _renderLocationsValue = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return <Input type="number" placeholder={"Value"} />
    }
    return this._renderLocationsSelect()
  }
  private _paymentMethodLocked = 0
  private _ensurePaymentMethodList = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return
    }
    if (!this.state.paymentMethodList && this._paymentMethodLocked === 0) {
      this._paymentMethodLocked++
      sbListPaymentMethod({ integration_uuid: this.props.integration.uuid! })
        .then(list => {
          this.setState({ paymentMethodList: list.data.data?.Data || [] })
        })
        .catch(e => {
          console.error(e)
          const wrapped = e instanceof Error ? e : new Error(e.message)
          this.setState({ paymentMethodList: wrapped })
        })
        .finally(() => this._paymentMethodLocked--)
    }
  }
  private _renderPaymentMethodSelect = () => {
    this._ensurePaymentMethodList()
    return this.state.paymentMethodList instanceof Error
      ? <Input type="number" placeholder={"Value"} />
      : <Select
        showSearch
        filterOption={(input, option) =>
          (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
        }
        loading={this.state.paymentMethodList == null}
        options={this.state.paymentMethodList?.map(item => {
          return {
            label: item.Name,
            value: item.Id
          }
        })}
      />
  }
  private _renderPaymentMethodId = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return <Input type="number" placeholder={"Value"} />
    }
    return this._renderPaymentMethodSelect()
  }
  private _importStatusLocked = 0
  private _ensureImportStatusList = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return
    }
    if (!this.state.importStatusList && this._importStatusLocked === 0) {
      this._importStatusLocked++
      sbListImportStatus({ integration_uuid: this.props.integration.uuid! })
        .then(list => {
          this.setState({ importStatusList: list.data.data || [] })
        })
        .catch(e => {
          console.error(e)
          const wrapped = e instanceof Error ? e : new Error(e.message)
          this.setState({ importStatusList: wrapped })
        })
        .finally(() => this._importStatusLocked--)
    }
  }
  private _renderImportStatusSelect = () => {
    this._ensureImportStatusList()
    return this.state.importStatusList instanceof Error
      ? <Input type="number" placeholder={"Value"} />
      : <Select
        showSearch
        filterOption={(input, option) =>
          (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
        }
        loading={this.state.importStatusList == null}
        options={this.state.importStatusList?.map(item => {
          return {
            label: item.name,
            value: item.value
          }
        })}
      />
  }
  private _renderImportStatusValue = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return <Input type="number" placeholder={"Value"} />
    }
    return this._renderImportStatusSelect()
  }
  private _transitionStatusLocked = 0
  private _ensureTransitionStatusList = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return
    }
    if (!this.state.transitionStatusList && this._transitionStatusLocked === 0) {
      this._transitionStatusLocked++
      sbListTransitionStatus({ integration_uuid: this.props.integration.uuid! })
        .then(list => {
          this.setState({ transitionStatusList: list.data.data || [] })
        })
        .catch(e => {
          console.error(e)
          const wrapped = e instanceof Error ? e : new Error(e.message)
          this.setState({ transitionStatusList: wrapped })
        })
        .finally(() => this._transitionStatusLocked--)
    }
  }
  private _renderTransitionStatusSelect = () => {
    this._ensureTransitionStatusList()
    return this.state.transitionStatusList instanceof Error
      ? <Input type="number" placeholder={"Value"} />
      : <Select
        showSearch
        filterOption={(input, option) =>
          (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
        }
        loading={this.state.transitionStatusList == null}
        options={this.state.transitionStatusList?.map(item => {
          return {
            label: item.name,
            value: item.value
          }
        })}
      />
  }
  private _adoptionStatusLocked = 0
  private _ensureAdoptionStatusList = () => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return
    }
    if (!this.state.adoptionStatusList && this._adoptionStatusLocked === 0) {
      this._adoptionStatusLocked++
      sbListAdoptionStatus({ integration_uuid: this.props.integration.uuid! })
        .then(list => {
          this.setState({ adoptionStatusList: list.data.data || [] })
        })
        .catch(e => {
          console.error(e)
          const wrapped = e instanceof Error ? e : new Error(e.message)
          this.setState({ adoptionStatusList: wrapped })
        })
        .finally(() => this._adoptionStatusLocked--)
    }
  }
  private _renderAdoptionStatusSelect = () => {
    this._ensureAdoptionStatusList()
    return this.state.adoptionStatusList instanceof Error
      ? <Input type="number" placeholder={"Value"} />
      : <Select
        showSearch
        filterOption={(input, option) =>
          (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
        }
        loading={this.state.adoptionStatusList == null}
        options={this.state.adoptionStatusList?.map(item => {
          return {
            label: item.name,
            value: item.value
          }
        })}
      />
  }
  private _getStatusValueOptions = () => {
    const allOptions: any[] = []

    if (this.state.importStatusList && !(this.state.importStatusList instanceof Error)) {
      this.state.importStatusList.forEach(item => {
        allOptions.push({
          label: `${item.name}`,
          value: item.value
        })
      })
    }

    if (this.state.transitionStatusList && !(this.state.transitionStatusList instanceof Error)) {
      this.state.transitionStatusList.forEach(item => {
        allOptions.push({
          label: `${item.name}`,
          value: item.value
        })
      })
    }

    // Add adoption status options
    if (this.state.adoptionStatusList && !(this.state.adoptionStatusList instanceof Error)) {
      this.state.adoptionStatusList.forEach(item => {
        allOptions.push({
          label: `${item.name}`,
          value: item.value
        })
      })
    }

    return allOptions
  }


  private _renderStatusMapValue = (itemKey?: number, parentKey?: number, form?: any) => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return <Input type="number" placeholder={"Value"} />
    }


    if (!form || itemKey === undefined || parentKey === undefined) {
      return <Input type="number" placeholder={"Value"} />
    }

    // Get all form values
    const formValues = form.getFieldsValue()
    const statusMapArray = formValues.status_map || []

    // Access the specific status map item and status within it
    const statusMapItem = statusMapArray[parentKey]
    const statusArray = statusMapItem?.status || []
    const currentStatusItem = statusArray[itemKey]

    const selectedType = currentStatusItem?.type

    if (selectedType === 'AVAILABLE') {
      return this._renderImportStatusSelect()
    } else if (selectedType === 'APPROVED') {
      return this._renderTransitionStatusSelect()
    } else if (selectedType === 'ADOPTED') {
      return this._renderAdoptionStatusSelect()
    }

    return <Input type="number" placeholder={"Value"} />
  }
  private _renderBaseStatusMapValue = (field: any, fieldName: string[], props: any) => {
    if (this.props.integration?.partner_key !== "SHELTER_BUDDY") {
      return <Input type="number" placeholder={"Value"} />
    }

    if (!props.form) {
      return <Input type="number" placeholder={"Value"} />
    }

    const formValues = props.form.getFieldsValue()
    const baseStatusMapArray = formValues.base_status_map || []

    const itemIndex = fieldName[0]
    const currentItem = baseStatusMapArray[itemIndex]

    const selectedType = currentItem?.map_type

    if (selectedType === 'AVAILABLE') {
      return this._renderImportStatusSelect()
    } else if (selectedType === 'APPROVED') {
      return this._renderTransitionStatusSelect()
    } else if (selectedType === 'ADOPTED') {
      return this._renderAdoptionStatusSelect()
    }

    return <Input type="number" placeholder={"Value"} />
  }
  private renderDynamicReceiptMap = () => {
    if (!this.formRef || this.props.integration?.partner_key !== "SHELTER_BUDDY") return null

    return (
      <>
        <Typography.Title level={5}>Dynamic Receipt Account Map</Typography.Title>
        <DynamicFields
          name={['dyn_receipt_account_map']}
          form={this.formRef}
          ref={(ref) => this.statusFieldsRef = ref}
          baseStruct={{
            dyn_receipt_account_map: {
              collapsed: true,
              title: "Dynamic Receipt Account Map",
              fields: {
                map_type: {
                  label: 'Checkout Type',
                  type: 'select',
                  render: this._renderSelectCheckoutItem,
                },
                value: {
                  label: 'Rules',
                  type: 'object',
                  allowReorder: true,
                  fields: {
                    condition: {
                      label: 'Condition',
                      type: 'text'
                    },
                    value: {
                      label: 'Value',
                      type: 'custom',
                      render: this._renderSelectReceiptAccount,
                    },
                    description: {
                      label: 'Description (optional)',
                      type: "text"
                    }
                  }
                }
              }
            }
          }}
        />

        <Divider />
      </>
    )
  }

  render() {
    const { settings, total, loading } = this.props
    const {
      loaded,
      loadError,
      saving,
      includeFiltersMap,
      excludeFiltersMap,
      receiptAccountMap,
      dynReceiptAccountMap,
      statusMap,
      baseStatusMap,
      speciePricingMap,
      contractOptionsMap,
      locationsList,
      acceptableStatusList,
      searchStatusList,
      searchGroupList
    } = this.state


    if (!loaded) {
      return <Skeleton />
    }
    if (loadError) {
      return (
        <Result
          status="500"
          title="Could not load settings"
          subTitle={loadError}
        />
      )
    }

    const values: any = {
      ...settings,
      filters: includeFiltersMap,
      exclude_filters: excludeFiltersMap,

      receipt_account_map: receiptAccountMap,
      dyn_receipt_account_map: dynReceiptAccountMap,
      status_map: statusMap,
      base_status_map: baseStatusMap,
      specie_pricing_map: speciePricingMap,
      contract_options_map: contractOptionsMap,
      locations_list: locationsList,
      acceptable_status_list: acceptableStatusList,
      search_status_list: searchStatusList,
      search_group_list: searchGroupList
    }

    return (
      <IntegrationConsumer>
        {(context) => {
          return (
            <Form
              name="integration_settings"
              initialValues={values}
              layout="vertical"
              ref={ref => {
                if (!this.formRef) {
                  this.formRef = ref
                  this.forceUpdate()
                }
              }}
              style={{
                marginTop: 30
              }}
              onFieldsChange={this.handleFieldsChange}
              onFinish={this.handleSave}
            >

              <Row gutter={24}>
                <Col xs={24} md={10}>
                  <Form.Item
                    label="Foster Pets"
                    name="foster_pets"
                  >
                    {this.renderFosterPets()}
                  </Form.Item>
                </Col>

                <Col xs={24} md={10}>
                  <Form.Item
                    label="Foster User"
                    name="foster_user_uuid"
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} md={4}>
                  <Form.Item
                    label="Start Visibility"
                    name="start_visibility"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              {this.renderCustomSettings(context)}

              <Divider />

              {this.renderFilters()}

              {this.renderMappings()}

              <Divider />

              {this.renderDynamicReceiptMap()}

              <Typography.Title level={4}>Total pets filtered: {loading ? <LoadingOutlined /> : total}</Typography.Title>

              <Form.Item style={{ marginTop: 32 }}>
                <Button type="primary" size="large" htmlType="submit" loading={saving} block>
                  Save
                </Button>
              </Form.Item>
            </Form>
          )
        }}
      </IntegrationConsumer>
    )
  }
}
